import React, { useCallback } from 'react'
import { useDocuments } from './client-provider'
import { IChangeRequest } from '@reapitform/dashboard-ui'

interface IAgreementState {
  sectionsError?: (number | undefined)[]
  sectionsChangeRequested?: (IChangeRequest[] | undefined)[]
  hasError?: boolean
  isFinishedLoadSections?: boolean
  isEditing?: boolean
  editingOn?: string | number
  activeMenu?: string
}

interface IAgreementContext {
  state: IAgreementState
  setSectionErrorState: (key: number, errorCount: number) => void
  setSectionChangeRequested: (index: number, changeRequested: IChangeRequest[]) => void
  setIsEditing: (isEditing: boolean, edittedOn?: string | number) => void
  setActiveMenu: (sectionId: string, isActive: boolean) => void
}

const AgreementContext = React.createContext<IAgreementContext>({
  setSectionErrorState: () => {},
  setSectionChangeRequested: () => {},
  setIsEditing: () => {},
  setActiveMenu: () => {},
  state: {},
})

export const useAgreementState = () => React.useContext(AgreementContext)

export const AgreementStateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [sectionsError, setSectionsError] = React.useState<IAgreementState['sectionsError']>()
  const [sectionsChangeRequested, setSectionsChangeRequested] =
    React.useState<IAgreementState['sectionsChangeRequested']>()

  const [isEditing, setIsEditing] = React.useState<boolean>(false)
  const [editingOn, setEditingOn] = React.useState<string | number>()

  const [activeMenuState, setActiveMenuState] = React.useState<string[]>([])
  const [lastActiveState, setLastActiveMenuState] = React.useState<string[]>([])

  const documents = useDocuments()

  const isDoneCheckSectionError: boolean =
    sectionsError !== undefined && !sectionsError.find((sectionErrorCount) => sectionErrorCount === undefined)
  const isDoneCheckChangeRequested: boolean =
    sectionsChangeRequested !== undefined &&
    !sectionsChangeRequested.find((sectionChangeRequest) => sectionChangeRequest === undefined)

  const hasSectionError = Boolean(sectionsError?.find((sectionErrorCount) => (sectionErrorCount ?? 0) > 0))
  const hasChangeRequest = Boolean(
    sectionsChangeRequested?.find((sectionChangeRequest) => (sectionChangeRequest?.length ?? 0) > 0),
  )

  const isFinishedLoadSections: boolean = isDoneCheckChangeRequested && isDoneCheckSectionError

  const hasError: boolean | undefined = isFinishedLoadSections ? hasSectionError || hasChangeRequest : undefined

  React.useEffect(() => {
    if (!documents?.sections) return

    const defaultSectionError = documents.sections.map(() => undefined)
    setSectionsError((prevError) => prevError ?? defaultSectionError)
  }, [documents?.sections])

  const setSectionErrorState = useCallback((key: number, errorCount: number) => {
    setSectionsError((prevError) => {
      const errors = prevError ? [...prevError] : (documents?.sections || []).map(() => undefined)
      errors.splice(key, 1, errorCount)

      return errors
    })
  }, [])

  const handleChangeRequestedSetup = useCallback((index: number, changeRequested: IChangeRequest[]) => {
    setSectionsChangeRequested((prevSectionChangeRequest) => {
      const currentChangeRequested = prevSectionChangeRequest
        ? [...prevSectionChangeRequest]
        : (documents?.sections || []).map(() => undefined)
      currentChangeRequested.splice(index, 1, changeRequested)
      return currentChangeRequested
    })
  }, [])

  const changeEditState = (isEditing: boolean, edittedOn?: string | number) => {
    setEditingOn(edittedOn)
    setIsEditing(isEditing)
  }

  const setActiveMenu = React.useCallback((sectionId: string, isActive: boolean) => {
    setActiveMenuState((prevActiveMenu: string[]) => {
      const currentActiveMenu = [...prevActiveMenu]

      if (isActive) return [...currentActiveMenu, sectionId]

      const activeMenuIndex = currentActiveMenu.findIndex((activeSectionId) => activeSectionId === sectionId)

      if (activeMenuIndex >= 0) {
        const deletedItem = currentActiveMenu.splice(activeMenuIndex, 1)
        setLastActiveMenuState(deletedItem)
      }
      return currentActiveMenu
    })
  }, [])

  const activeMenu: string | undefined = React.useMemo(
    () => activeMenuState[0] ?? lastActiveState[0],
    [activeMenuState, lastActiveState],
  )

  return (
    <AgreementContext.Provider
      value={{
        state: {
          sectionsError,
          isFinishedLoadSections,
          hasError,
          isEditing,
          editingOn,
          sectionsChangeRequested,
          activeMenu,
        },
        setSectionErrorState,
        setIsEditing: changeEditState,
        setSectionChangeRequested: handleChangeRequestedSetup,
        setActiveMenu,
      }}
    >
      {children}
    </AgreementContext.Provider>
  )
}
